import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    Calendar, Loader2, CheckCircle2, Video, Phone, MapPin, ArrowRight, Clock, Globe, MessageCircle, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Curated timezone list (top-of-the-world coverage). Native Intl.supportedValuesOf('timeZone')
// returns 400+ entries — noisy for a booking dropdown, so we ship a hand-picked set.
const TIMEZONES = [
    "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
    "America/Toronto", "America/Sao_Paulo", "UTC",
    "Europe/London", "Europe/Berlin", "Europe/Madrid", "Europe/Athens",
    "Africa/Lagos", "Africa/Johannesburg",
    "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Singapore", "Asia/Shanghai", "Asia/Tokyo",
    "Australia/Sydney", "Pacific/Auckland",
];

// Compact TZ label like "PT", "ET" via Intl if the browser supports it — else the raw name.
const tzShort = (iso, tz) => {
    try {
        const parts = new Intl.DateTimeFormat(undefined, { timeZone: tz, timeZoneName: "short" }).formatToParts(new Date(iso));
        return parts.find((p) => p.type === "timeZoneName")?.value || tz;
    } catch { return tz; }
};

const fmtSlotTime = (iso, tz) => {
    try {
        return new Intl.DateTimeFormat(undefined, { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
    } catch { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
};
const fmtSlotDay = (iso, tz) => {
    try {
        return new Intl.DateTimeFormat(undefined, { timeZone: tz, weekday: "long", month: "short", day: "numeric" }).format(new Date(iso));
    } catch { return new Date(iso).toDateString(); }
};

/** Public booking page — /book/:slug. No auth required. */
const PublicBooking = () => {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const preferredIso = searchParams.get("prefer");
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", notes: "" });
    const reservationToken = searchParams.get("res");
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [notFound, setNotFound] = useState(false);
    // Default to the visitor's browser timezone. Explicit override via dropdown.
    const [tz, setTz] = useState(() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
        catch { return "UTC"; }
    });

    useEffect(() => {
        setLoading(true);
        // Pass ?res= to the backend so a reserved-slot link both shows the slot
        // AND returns prefill payload (candidate name/email).
        const qs = reservationToken ? `?res=${encodeURIComponent(reservationToken)}` : "";
        api.get(`/book/${slug}${qs}`)
            .then(({ data }) => {
                setPage(data);
                // Auto-fill the form when the backend recognised the token.
                if (data.prefill?.candidate_email) {
                    setForm((f) => ({
                        ...f,
                        name: f.name || data.prefill.candidate_name || "",
                        email: f.email || data.prefill.candidate_email || "",
                    }));
                }
            })
            .catch((err) => {
                if (err?.response?.status === 404) setNotFound(true);
                else toast.error(formatApiError(err));
            })
            .finally(() => setLoading(false));
    }, [slug, reservationToken]);

    // Auto-select + scroll to the ?prefer=<iso> slot when the page loads.
    // Falls back to the server-provided preferred_iso (which the backend fills
    // when the ?res=<token> matches an active reservation, even if the URL
    // omitted ?prefer=). Surface a friendly toast when the slot's already gone.
    useEffect(() => {
        const target = preferredIso || page?.preferred_iso;
        if (!target || !page?.slots) return;
        if (page.slots.includes(target)) {
            setSelected(target);
            requestAnimationFrame(() => {
                document.querySelector(`[data-testid="slot-${target}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
            });
        } else {
            toast.info("That specific time is no longer available — please pick another below.");
        }
    }, [preferredIso, page?.slots, page?.preferred_iso]);

    const groupedByDay = useMemo(() => {
        if (!page?.slots) return {};
        // Group by local date in the selected timezone — so a slot at 23:30 UTC
        // that's 15:30 in PT lives under the PT calendar day, not the UTC one.
        return page.slots.reduce((acc, iso) => {
            let day;
            try {
                day = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
            } catch { day = iso.slice(0, 10); }
            (acc[day] = acc[day] || []).push(iso);
            return acc;
        }, {});
    }, [page, tz]);

    const tzHint = useMemo(() => (page?.slots?.[0] ? tzShort(page.slots[0], tz) : tz), [page, tz]);

    const book = async () => {
        if (!selected) return toast.error("Pick a time first.");
        if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email required.");
        setSubmitting(true);
        try {
            await api.post(`/book/${slug}`, {
                start_iso: selected,
                ...form,
                ...(reservationToken ? { reservation_token: reservationToken } : {}),
            });
            setConfirmed(true);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    if (loading) return <div className="py-24 flex justify-center"><Loader2 size={20} className="animate-spin text-emerald-500" /></div>;
    if (notFound) return (
        <div className="max-w-md mx-auto text-center py-24 px-6">
            <Calendar size={26} className="mx-auto text-emerald-500" />
            <h1 className="mt-4 font-heading font-semibold text-2xl text-foreground">Booking page not found</h1>
            <p className="mt-2 text-sm text-muted-foreground">The link you used may have expired.</p>
        </div>
    );
    if (!page) return null;

    const LocationIcon = page.location_type === "phone" ? Phone : page.location_type === "in_person" ? MapPin : Video;

    if (confirmed) {
        return (
            <div data-testid="booking-confirmed" className="max-w-md mx-auto text-center py-24 px-6">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:ring-emerald-500/30">
                    <CheckCircle2 size={24} className="text-emerald-600 dark:text-emerald-400" />
                </div>
                <h1 className="font-heading font-semibold text-2xl text-foreground">You&apos;re booked!</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    A confirmation email is on its way. See you on <strong className="text-foreground">{fmtSlotDay(selected, tz)} at {fmtSlotTime(selected, tz)} ({tzHint})</strong>.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="public-booking-page" className="max-w-4xl mx-auto px-6 py-12">
            <Helmet><title>Book with {page.company} · Simonara</title></Helmet>
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <p className="overline">Interview scheduler</p>
                    <h1 className="mt-1 font-heading font-medium text-3xl text-foreground">Book time with {page.company}</h1>
                    <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {page.slot_minutes} min</span>
                        <span className="inline-flex items-center gap-1.5 capitalize"><LocationIcon size={14} /> {page.location_type.replace("_", " ")}</span>
                    </div>
                </div>
                <div className="min-w-[240px]">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                        <Globe size={11} /> You&apos;re viewing in <strong className="text-foreground" data-testid="booking-tz-hint">{tzHint}</strong>
                    </div>
                    <Select value={tz} onValueChange={setTz}>
                        <SelectTrigger data-testid="booking-tz-select" className="bg-background text-xs h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {TIMEZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 card-soft p-5">
                    <p className="overline mb-3">Pick a time</p>
                    {Object.keys(groupedByDay).length === 0 ? (
                        <div className="text-sm text-muted-foreground">No available slots. Please try again later.</div>
                    ) : (
                        <div className="space-y-5 max-h-[520px] overflow-y-auto pr-2">
                            {Object.entries(groupedByDay).map(([day, slots]) => (
                                <div key={day}>
                                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                        {fmtSlotDay(slots[0], tz)}
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {slots.map((iso) => {
                                            const active = selected === iso;
                                            return (
                                                <button
                                                    key={iso}
                                                    onClick={() => setSelected(iso)}
                                                    data-testid={`slot-${iso}`}
                                                    className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${active ? "bg-emerald-600 text-white border-emerald-600" : "border-border bg-card text-foreground hover:border-emerald-500/40"}`}
                                                >
                                                    {fmtSlotTime(iso, tz)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2 card-soft p-5 space-y-3">
                    <p className="overline">Your details</p>
                    {selected && (
                        <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-0" data-testid="booking-selected-badge">
                            <Clock size={10} className="inline mr-1" /> {fmtSlotDay(selected, tz)}, {fmtSlotTime(selected, tz)} ({tzHint})
                        </Badge>
                    )}
                    <div>
                        <Label>Full name</Label>
                        <Input data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input type="email" data-testid="booking-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Anything to share? (optional)</Label>
                        <Textarea data-testid="booking-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5" placeholder="Timezone, questions, etc." />
                    </div>
                    <button
                        onClick={book}
                        disabled={submitting || !selected || !form.name || !form.email}
                        data-testid="booking-submit"
                        className="btn-green w-full disabled:opacity-60"
                    >
                        {submitting ? <><Loader2 size={13} className="mr-1.5 inline animate-spin" /> Confirming…</> : <>Confirm booking <ArrowRight size={13} className="ml-1.5 inline" /></>}
                    </button>
                    {reservationToken && page?.prefill && (
                        <ReservationChat slug={slug} resToken={reservationToken} />
                    )}
                </div>
            </div>
        </div>
    );
};

// Inline chat widget on the booking form — only shown when the visitor
// arrived via a reservation link (so we know exactly who they are and can
// route messages back to the employer without any auth).
const ReservationChat = ({ slug, resToken }) => {
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [open, setOpen] = useState(false);
    const scroller = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/book/${slug}/messages?res=${encodeURIComponent(resToken)}`);
            setMessages(data.results || []);
        } catch { /* silent — public endpoint, keeps polling. */ }
    }, [slug, resToken]);

    useEffect(() => {
        load();
        // 15s poll — cheap on the server and keeps the thread live enough.
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load]);

    // Auto-scroll to bottom on new messages.
    useEffect(() => {
        if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body) return;
        setSending(true);
        try {
            const { data } = await api.post(`/book/${slug}/messages`, { res: resToken, body });
            setMessages((prev) => [...prev, data.message]);
            setDraft("");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSending(false); }
    };

    return (
        <div data-testid="booking-chat" className="mt-4 rounded-xl border border-emerald-500/40 bg-emerald-500/5 overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="booking-chat-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-emerald-800 dark:text-emerald-300 hover:bg-emerald-500/10"
            >
                <span className="inline-flex items-center gap-1.5">
                    <MessageCircle size={12} /> Chat with the team {messages.length > 0 && <span className="ml-1 rounded-full bg-emerald-600 text-white text-[9px] px-1.5 py-0.5">{messages.length}</span>}
                </span>
                <span className="text-[10px] opacity-70">{open ? "hide" : "show"}</span>
            </button>
            {open && (
                <div className="border-t border-emerald-500/20 p-3 space-y-2">
                    <div ref={scroller} data-testid="booking-chat-thread" className="max-h-40 overflow-y-auto space-y-1.5 text-xs">
                        {messages.length === 0 ? (
                            <p className="text-muted-foreground italic text-center py-3">No messages yet — say hi if you have questions before booking.</p>
                        ) : messages.map((m) => (
                            <div
                                key={m.id}
                                data-testid={`booking-chat-msg-${m.id}`}
                                className={`flex ${m.sender === "candidate" ? "justify-end" : "justify-start"}`}
                            >
                                <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 ${m.sender === "candidate" ? "bg-emerald-600 text-white" : "bg-white dark:bg-slate-900 border border-border text-foreground"}`}>
                                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                                    <div className={`text-[9px] mt-0.5 flex items-center gap-1 ${m.sender === "candidate" ? "text-emerald-100 justify-end" : "text-muted-foreground"}`}>
                                        <span>{new Date(m.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>
                                        {m.sender === "candidate" && m.read_at && (
                                            <span data-testid={`booking-chat-seen-${m.id}`} title={`Seen ${new Date(m.read_at).toLocaleString()}`} className="ml-0.5">✓ seen</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <form onSubmit={send} className="flex gap-1.5">
                        <input
                            type="text"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            data-testid="booking-chat-input"
                            placeholder="Type a message…"
                            className="flex-1 text-xs rounded-md border border-border bg-background px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                        <button
                            type="submit"
                            disabled={sending || !draft.trim()}
                            data-testid="booking-chat-send"
                            className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 text-xs disabled:opacity-60"
                        >
                            {sending ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default PublicBooking;
